import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/core/hooks";
import {
  initApp,
  setPointId,
  setMaxPreorderAmount,
  setReadOnly,
  setDebug,
  setPendingEditOrderId,
} from "@/core/app/reducer";
import { isOrderId } from "@/core/orders/guards";
import { useGuestPoint } from "@/core/point/hooks";
import { LoadingAnimation } from "@/components/molecules";

interface DataLoaderProps {
  children: React.ReactNode;
}

const ErrorContainer: React.FC<{
  title: string;
  message: string;
  action?: { label: string; onClick: () => void };
}> = ({ title, message, action }) => (
  <div style={{ padding: "20px", textAlign: "center", color: "red" }}>
    <h3>{title}</h3>
    <p>{message}</p>
    {action && (
      <button
        onClick={action.onClick}
        style={{ padding: "10px 20px", marginTop: "10px" }}
      >
        {action.label}
      </button>
    )}
  </div>
);

/**
 * Читает параметры, которые SDK передаёт в URL iframe, и кладёт их в
 * app-слайс. Это UI-состояние, оно остаётся.
 *
 * Ф4б: точка резолвится гостевым маршрутом — `useGuestPoint` кладёт
 * `brandId` в app-слайс и отвечает за три состояния до рендера меню:
 * точки не назвали, точку не удалось разрешить (404 либо негодный `pointId`
 * в ссылке), точка ещё грузится.
 *
 * Ф7: восстановлением корзины занимается useOrderRestore. Здесь остаётся
 * только чтение параметра `orderId` из ссылки — задания на редактирование.
 * Прежний эффект, стиравший `persistedOrderId` на каждом рендере, снят: на
 * этом поле держится всё восстановление.
 */
export const DataLoader: React.FC<DataLoaderProps> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  // Get URL parameters - only pointId is required now
  const urlPointId = searchParams.get("pointId");
  const urlMaxPreorderAmount = searchParams.get("maxPreorderAmount");
  const urlIsReadOnly = searchParams.get("isReadOnly");
  const urlDebug = searchParams.get("debug");
  const urlOrderId = searchParams.get("orderId");

  // Get Redux state (including persisted values)
  const { initialized, pointId: storePointId } = useAppSelector(
    (state) => state.app
  );

  // Determine effective parameters with priority: URL > persist store > null
  const effectivePointId = urlPointId || storePointId;

  // Точка — единственный источник brandId: хук кладёт его в app-слайс сам.
  const { isLoading: pointLoading, isUnavailable: pointUnavailable } =
    useGuestPoint(effectivePointId);

  // Initialize app with new logic
  useEffect(() => {
    if (effectivePointId) {
      // Set pointId in store
      if (urlPointId) {
        dispatch(setPointId(urlPointId));
      }

      // Set maxPreorderAmount in store if provided
      if (urlMaxPreorderAmount) {
        const maxAmount = parseInt(urlMaxPreorderAmount, 10);
        if (!isNaN(maxAmount) && maxAmount > 0) {
          dispatch(setMaxPreorderAmount(maxAmount));
        }
      }

      // Set isReadOnly in store if provided
      if (urlIsReadOnly === "true") {
        dispatch(setReadOnly(true));
      }

      // Set debug in store if provided
      if (urlDebug === "true") {
        dispatch(setDebug(true));
      }

      // Заказ из ссылки — задание на редактирование. Гард «uuid или игнор»:
      // негодное значение молча не порождает запрос (§3.5 спеки Ф7).
      if (isOrderId(urlOrderId)) {
        dispatch(setPendingEditOrderId(urlOrderId));
      }

      if (!initialized) {
        dispatch(initApp());
      }
    }
  }, [
    effectivePointId,
    urlPointId,
    urlMaxPreorderAmount,
    urlIsReadOnly,
    urlDebug,
    urlOrderId,
    initialized,
    dispatch,
  ]);

  // Show error if no pointId available
  if (!effectivePointId) {
    return (
      <ErrorContainer
        title="Не указана точка"
        message="Откройте меню по ссылке из приложения — в ней передаётся идентификатор точки."
      />
    );
  }

  // Несуществующая, выключенная и адресованная негодным id точка для гостя
  // неразличимы — сообщение покрывает все три случая.
  if (pointUnavailable) {
    return (
      <ErrorContainer
        title="Точка недоступна"
        message="Заведение не найдено или временно не принимает заказы."
      />
    );
  }

  // Дети читают pointId из СЛАЙСА (useMenuData через useMenu), а слайс
  // догоняет URL только эффектом выше — то есть на рендер позже. Без этой
  // сверки повторный вход с уже прогретым кэшем точки монтировал бы меню
  // со старым персистнутым pointId и успевал отправить полный круг
  // /guest/* за прошлую точку.
  if (pointLoading || storePointId !== effectivePointId) {
    return <LoadingAnimation />;
  }

  return <>{children}</>;
};
